import React, { useState, useEffect, useRef } from 'react';
import { Check, Loader2, Search, Edit3, FileText, Hash, Image as ImageIcon, Share2, Play, RefreshCw, Cpu, Save, ExternalLink, X, ThumbsUp, MessageSquare, Repeat, Send, AlertCircle } from 'lucide-react';

// --- N8N CONFIGURATION ---
const N8N_CONFIG = {
  TRIGGER_URL: "https://n8n-prod.easydialai.com/webhook/b6a0831d-97b8-44d9-87fd-68c94aab0a88", 
};

const LEVELS = [
  { id: 1, title: "Topic Found", icon: Search, desc: "Identifying viral topic..." },
  { id: 2, title: "Content Finalizing", icon: Edit3, desc: "Structuring hook & intent..." },
  { id: 3, title: "Preparing Caption", icon: FileText, desc: "Drafting copy with NLP..." },
  { id: 4, title: "Preparing Hashtag", icon: Hash, desc: "Optimizing reach..." },
  { id: 5, title: "Preparing Image", icon: ImageIcon, desc: "Rendering assets..." },
  { id: 6, title: "Saving to Memory", icon: Save, desc: "Archiving to database..." },
];

export default function WorkflowMap() {
  const [currentLevelId, setCurrentLevelId] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [hoveredLevel, setHoveredLevel] = useState(null);
  const [isMobile, setIsMobile] = useState(false); // Track screen size
  
  const [finalPostData, setFinalPostData] = useState(null);
  const [showPostPreview, setShowPostPreview] = useState(false);
  
  const resultRef = useRef(null);
  const previewRef = useRef(null);
  const serverDataRef = useRef(null); 
  const animationIntervalRef = useRef(null);

  // --- RESPONSIVENESS LISTENER ---
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Check initially
    handleResize();
    
    // Add listener
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- N8N INTEGRATION LOGIC ---
  const startWorkflow = async () => {
    if (isProcessing) return; 
    
    setIsProcessing(true);
    setIsFinished(false);
    setCurrentLevelId(1);
    setFinalPostData(null);
    setShowPostPreview(false);
    serverDataRef.current = null;

    startSmoothAnimationLoop();

    try {
      console.log("🚀 Starting Workflow...", N8N_CONFIG.TRIGGER_URL);
      
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            trigger: 'web_ui',
            timestamp: new Date().toISOString() 
        })
      };

      let response;
      let responseData = null;

      try {
          response = await fetch(N8N_CONFIG.TRIGGER_URL, requestOptions);

          if (response.status === 404 && N8N_CONFIG.TRIGGER_URL.includes('webhook-test')) {
            const prodUrl = N8N_CONFIG.TRIGGER_URL.replace('webhook-test', 'webhook');
            console.warn("⚠️ Test Webhook 404. Retrying with Production URL:", prodUrl);
            response = await fetch(prodUrl, requestOptions);
          }

          if (response.ok) {
             responseData = await response.json();
             console.log("✅ Data Received from n8n:", responseData);
             serverDataRef.current = responseData;
          } else {
             throw new Error(`HTTP error! status: ${response.status}`);
          }

      } catch (fetchError) {
          if (fetchError.name === 'TypeError' || fetchError.message.includes('Failed to fetch')) {
             console.warn("⚠️ Standard fetch failed (CORS). Retrying with no-cors mode...");
             await fetch(N8N_CONFIG.TRIGGER_URL, { ...requestOptions, mode: 'no-cors' });
             serverDataRef.current = {}; 
          } else {
             throw fetchError;
          }
      }

    } catch (error) {
      console.error("❌ Workflow Failed:", error);
      clearInterval(animationIntervalRef.current);
      setIsProcessing(false);
      alert(`Connection Error: ${error.message}\n\nPlease check your n8n workflow.`);
    }
  };

  const startSmoothAnimationLoop = () => {
    if (animationIntervalRef.current) clearInterval(animationIntervalRef.current);

    animationIntervalRef.current = setInterval(() => {
      setCurrentLevelId(prevLevel => {
        if (prevLevel < LEVELS.length) {
          return prevLevel + 1;
        }
        if (prevLevel === LEVELS.length) {
          if (serverDataRef.current) {
            clearInterval(animationIntervalRef.current);
            finishWorkflow(serverDataRef.current);
            return prevLevel + 1; 
          } else {
            return prevLevel; 
          }
        }
        return prevLevel;
      });
    }, 2000); 
  };

  const finishWorkflow = (realData) => {
    setIsProcessing(false);
    setIsFinished(true);
    
    setFinalPostData({
      author: realData?.author || "EasyDial Agent",
      authorRole: "Autonomous Creator",
      time: "Just now",
      caption: realData?.caption || "🚀 Workflow complete! Content generated successfully.",
      imageUrl: realData?.imageUrl || null, 
      linkedInUrl: realData?.linkedInUrl || "#"
    });
  };

  useEffect(() => {
    if (isFinished && resultRef.current) {
      setTimeout(() => {
        resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [isFinished]);

  useEffect(() => {
    if (showPostPreview && previewRef.current) {
       setTimeout(() => {
        previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [showPostPreview]);

  const handleReset = () => {
    setCurrentLevelId(1);
    setIsProcessing(false);
    setIsFinished(false);
    setShowPostPreview(false);
    serverDataRef.current = null;
    if (animationIntervalRef.current) clearInterval(animationIntervalRef.current);
  };

  const progressPercent = Math.min(100, Math.max(0, ((currentLevelId - 1) / (LEVELS.length - 1)) * 100));

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 flex flex-col items-center justify-center relative overflow-x-hidden selection:bg-purple-100 py-4 md:py-0">
      
      {/* Background Grid */}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-40" 
           style={{ 
             backgroundImage: 'linear-gradient(#cbd5e1 1px, transparent 1px), linear-gradient(to right, #cbd5e1 1px, transparent 1px)', 
             backgroundSize: '40px 40px' 
           }}>
      </div>
      
      <div className="absolute top-0 left-0 w-48 md:w-96 h-48 md:h-96 bg-purple-200 rounded-full blur-[60px] md:blur-[128px] opacity-40 mix-blend-multiply filter pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-48 md:w-96 h-48 md:h-96 bg-blue-200 rounded-full blur-[60px] md:blur-[128px] opacity-40 mix-blend-multiply filter pointer-events-none" />

      {/* Top Controls - Fixed on mobile, Absolute on Desktop */}
      <div className="fixed md:absolute top-0 left-0 right-0 md:top-8 md:right-8 md:left-auto z-50 flex items-center justify-between md:justify-end gap-3 p-4 bg-white/90 md:bg-transparent backdrop-blur-md md:backdrop-blur-none border-b md:border-none border-slate-200 shadow-sm md:shadow-none">
        {/* Mobile Title in Nav */}
        <div className="md:hidden flex items-center gap-2">
            <div className="w-8 h-8 bg-[#7c3aed] rounded-lg flex items-center justify-center text-white font-bold">ED</div>
            <span className="font-bold text-slate-800">EasyDial.ai</span>
        </div>

        <div className="flex gap-2">
            <button 
            onClick={handleReset}
            className="p-2 md:p-3 rounded-xl bg-white/80 backdrop-blur border border-slate-200 text-slate-400 hover:text-[#7c3aed] hover:border-[#7c3aed] transition-all shadow-sm hover:shadow-md active:scale-95"
            title="Reset"
            >
            <RefreshCw size={18} />
            </button>
            <button 
            onClick={startWorkflow}
            disabled={isProcessing}
            className={`
                group px-4 py-2 md:px-6 md:py-3 rounded-xl font-bold text-xs md:text-sm transition-all flex items-center gap-2 shadow-xl active:scale-95 border
                ${isProcessing 
                ? 'bg-white border-purple-100 text-purple-400 cursor-not-allowed' 
                : 'bg-[#7c3aed] border-[#7c3aed] text-white hover:bg-[#6d28d9] shadow-purple-500/30'
                }
            `}
            >
            {isProcessing ? (
                <>Running <Loader2 size={14} className="animate-spin" /></>
            ) : (
                <>Start <span className="hidden md:inline">Workflow</span> <Play size={14} fill="currentColor" className="group-hover:translate-x-1 transition-transform" /></>
            )}
            </button>
        </div>
      </div>

      <div className="w-full max-w-6xl px-4 md:px-6 relative z-10 flex flex-col items-center pt-24 pb-10 md:py-20">
        
        {/* Header */}
        <div className="text-center mb-8 md:mb-16 animate-fade-in-up">
           <div className="inline-flex items-center gap-2 px-3 py-1 md:px-4 md:py-1.5 rounded-full bg-white text-[#7c3aed] text-[10px] md:text-xs font-bold uppercase tracking-wider mb-4 md:mb-6 border border-purple-100 shadow-sm">
             <Cpu size={12} /> Agent v2.4 Online
           </div>
           <h1 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-2 md:mb-3">
             EasyDial<span className="text-[#7c3aed]">.ai</span>
           </h1>
           <p className="text-slate-500 font-medium text-sm md:text-lg">Autonomous Content Pipeline</p>
        </div>

        {/* --- MAIN WORKFLOW PANEL --- */}
        <div className="w-full bg-white/60 backdrop-blur-xl border border-white/50 rounded-3xl p-6 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-8 transition-all duration-500">
            
            {/* The Pipeline Track */}
            <div className="relative w-full flex flex-col md:flex-row justify-between min-h-[450px] md:min-h-0 md:py-12 mb-4">
                
                {/* 1. Base Track */}
                <div className="absolute left-[21px] top-0 bottom-0 w-1.5 md:left-0 md:top-1/2 md:w-full md:h-1.5 md:-translate-y-1/2 md:bottom-auto bg-slate-200/50 rounded-full overflow-hidden" />
                
                {/* 2. Active Progress Track */}
                <div 
                    className="absolute bg-[#7c3aed] rounded-full z-0 transition-all duration-1000 ease-in-out shadow-[0_0_10px_rgba(124,58,237,0.4)] overflow-hidden left-[21px] top-0 w-1.5 md:left-0 md:top-1/2 md:h-1.5 md:w-auto md:-translate-y-1/2"
                    style={{ 
                        height: isMobile ? `${progressPercent}%` : '6px',
                        width: isMobile ? '6px' : `${progressPercent}%`,
                        maxHeight: '100%'
                     }}
                >
                    <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-transparent via-white/50 to-transparent w-full h-full animate-flow-fast" />
                </div>

                {/* 3. Steps Nodes */}
                {LEVELS.map((level, index) => {
                    const stepNum = index + 1;
                    const isCompleted = stepNum < currentLevelId;
                    const isActive = isProcessing && stepNum === currentLevelId;
                    
                    return (
                        <div 
                          key={level.id} 
                          className="flex md:flex-col items-center group cursor-pointer relative z-10 mb-8 md:mb-0 last:mb-0"
                          onMouseEnter={() => setHoveredLevel(stepNum)}
                          onMouseLeave={() => setHoveredLevel(null)}
                        >
                            {/* NODE CIRCLE */}
                            <div 
                                className={`
                                    relative w-12 h-12 md:w-16 md:h-16 rounded-2xl flex items-center justify-center border-2 transition-all duration-500 z-20 shrink-0
                                    ${isActive 
                                        ? 'bg-white border-[#7c3aed] text-[#7c3aed] scale-110 shadow-2xl shadow-purple-500/30' 
                                        : isCompleted 
                                            ? 'bg-[#7c3aed] border-[#7c3aed] text-white shadow-lg shadow-purple-500/20'
                                            : 'bg-white border-slate-200 text-slate-300'
                                    }
                                `}
                            >
                                {isActive && (
                                    <span className="absolute -inset-2 rounded-2xl border-2 border-[#7c3aed] opacity-20 animate-ping-slow" />
                                )}

                                {isCompleted ? (
                                    <Check size={20} strokeWidth={3} />
                                ) : isActive ? (
                                    <Loader2 size={20} className="animate-spin" />
                                ) : (
                                    <level.icon size={18} className="group-hover:scale-110 transition-transform" />
                                )}

                                {/* ACTIVE STATUS CARD */}
                                <div className={`
                                    absolute 
                                    left-16 top-1/2 -translate-y-1/2  /* Mobile: Push right of icon */
                                    md:left-1/2 md:top-auto md:bottom-full md:mb-4 md:-translate-x-1/2 md:translate-y-0 /* Desktop: Push above icon */
                                    bg-white px-3 py-2 md:px-5 md:py-3 rounded-xl border border-purple-100 shadow-[0_10px_40px_-10px_rgba(124,58,237,0.5)]
                                    flex items-center gap-2 md:gap-3 whitespace-nowrap z-50
                                    transition-all duration-500 ease-out 
                                    ${isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'}
                                `}>
                                    <div className="relative flex items-center justify-center w-2.5 h-2.5 md:w-3 md:h-3">
                                        <div className="absolute inset-0 bg-purple-400 rounded-full animate-ping opacity-20"></div>
                                        <div className="w-1.5 h-1.5 bg-[#7c3aed] rounded-full"></div>
                                    </div>
                                    <div className="flex flex-col text-left">
                                        <span className="text-[8px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-0.5">Processing</span>
                                        <span className="text-xs md:text-sm font-bold text-slate-800 leading-none">
                                            {level.title}
                                        </span>
                                    </div>
                                    
                                    {/* Arrow: Left for Mobile, Bottom for Desktop */}
                                    <div className="hidden md:block absolute top-full left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-r border-b border-purple-100 transform rotate-45 -translate-y-1.5"></div>
                                    <div className="block md:hidden absolute right-full top-1/2 -translate-y-1/2 w-2 h-2 bg-white border-l border-b border-purple-100 transform rotate-45 translate-x-1"></div>
                                </div>
                            </div>

                            {/* Step Name (Inactive State) */}
                            <div className={`
                                ml-4 md:ml-0 md:mt-6 font-mono text-xs md:text-[10px] font-bold tracking-widest uppercase md:text-center md:max-w-[80px] leading-tight transition-all duration-500 
                                ${isActive ? 'opacity-0' : isCompleted ? 'text-slate-400' : 'text-slate-300'}
                            `}>
                                {level.title}
                            </div>
                        </div>
                    );
                })}
            </div>
            
            {/* Status Footer */}
            <div className="mt-4 md:mt-8 text-center h-8">
                 <p className="text-xs md:text-sm text-slate-500 font-medium animate-pulse">
                    {isProcessing ? "Agent is working..." : (isFinished ? "Workflow Complete." : "System Idle. Ready to start.")}
                 </p>
            </div>
        </div>

        {/* --- 1. THE VIEW BUTTON --- */}
        {isFinished && !showPostPreview && (
            <div 
                ref={resultRef}
                className="mt-4 animate-fade-in-up flex flex-col items-center w-full"
            >
                <div className="w-1 h-8 md:h-12 bg-gradient-to-b from-[#7c3aed]/50 to-transparent mb-4" />
                
                <button 
                    onClick={() => setShowPostPreview(true)}
                    className="
                        group relative overflow-hidden bg-[#7c3aed] text-white px-8 py-4 md:px-10 md:py-5 rounded-full font-bold text-lg md:text-xl 
                        shadow-[0_20px_50px_rgba(124,58,237,0.3)] hover:shadow-[0_20px_50px_rgba(124,58,237,0.5)] 
                        hover:scale-105 active:scale-95 transition-all duration-300 flex items-center gap-3
                    "
                >
                    <span className="relative z-10 flex items-center gap-3">
                        View Generated Content <ExternalLink size={20} className="md:w-6 md:h-6" />
                    </span>
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
                </button>
            </div>
        )}

        {/* --- 2. THE POST PREVIEW --- */}
        {showPostPreview && finalPostData && (
             <div ref={previewRef} className="mt-8 animate-scale-in w-full max-w-lg mx-auto pb-12">
                <div className="bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
                    <div className="p-3 md:p-4 flex items-start justify-between border-b border-slate-100">
                        <div className="flex gap-3">
                            <div className="w-10 h-10 md:w-12 md:h-12 bg-slate-200 rounded-full overflow-hidden shrink-0">
                                <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Felix" alt="User" />
                            </div>
                            <div>
                                <h3 className="font-bold text-slate-900 text-sm">{finalPostData.author}</h3>
                                <p className="text-xs text-slate-500">{finalPostData.authorRole}</p>
                                <p className="text-xs text-slate-400 flex items-center gap-1">
                                    {finalPostData.time} • <span className="w-3 h-3 rounded-full bg-slate-300 flex items-center justify-center text-[8px] text-white">🌎</span>
                                </p>
                            </div>
                        </div>
                        <button onClick={() => setShowPostPreview(false)} className="text-slate-400 hover:text-slate-600">
                            <X size={20} />
                        </button>
                    </div>

                    <div className="p-3 md:p-4 pb-2">
                        <p className="text-xs md:text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                            {finalPostData.caption}
                        </p>
                    </div>

                    {finalPostData.imageUrl ? (
                        <div className="w-full mt-2 bg-slate-100">
                             <img src={finalPostData.imageUrl} alt="Generated Content" className="w-full h-auto object-cover" />
                        </div>
                    ) : (
                        <div className="w-full mt-2 bg-slate-50 border-2 border-dashed border-slate-200 p-8 flex flex-col items-center justify-center text-center">
                            <AlertCircle className="text-slate-300 mb-2" size={32} />
                            <p className="text-sm text-slate-400 font-medium">No image URL returned</p>
                        </div>
                    )}

                    <div className="px-3 md:px-4 py-3 flex items-center justify-between border-t border-slate-100 mt-2">
                         <button className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 text-xs md:text-sm font-medium">
                            <ThumbsUp size={16} className="md:w-[18px] md:h-[18px]" /> Like
                         </button>
                         <button className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 text-xs md:text-sm font-medium">
                            <MessageSquare size={16} className="md:w-[18px] md:h-[18px]" /> Comment
                         </button>
                         <button className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 text-xs md:text-sm font-medium">
                            <Repeat size={16} className="md:w-[18px] md:h-[18px]" /> Repost
                         </button>
                         <button className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 text-xs md:text-sm font-medium">
                            <Send size={16} className="md:w-[18px] md:h-[18px]" /> Send
                         </button>
                    </div>
                </div>

                <div className="text-center mt-6 mb-12 md:mb-0">
                    <a 
                        href={finalPostData.linkedInUrl} 
                        target="_blank" 
                        rel="noreferrer"
                        className="text-[#7c3aed] text-sm font-bold hover:underline"
                    >
                        Open original post on LinkedIn ↗
                    </a>
                </div>
             </div>
        )}

      </div>

      <style>{`
        @keyframes flow-fast {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        /* Vertical flow for mobile */
        @media (max-width: 768px) {
            @keyframes flow-fast {
                0% { transform: translateY(-100%); }
                100% { transform: translateY(100%); }
            }
        }
        .animate-flow-fast {
          animation: flow-fast 1.5s infinite linear;
        }
        .animate-ping-slow {
           animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        .animate-fade-in-up {
           animation: fadeInUp 0.8s ease-out forwards;
        }
        @keyframes fadeInUp {
           from { opacity: 0; transform: translateY(20px); }
           to { opacity: 1; transform: translateY(0); }
        }
        .animate-scale-in {
            animation: scaleIn 0.4s ease-out forwards;
        }
        @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.9) translateY(20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}